import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { Star, Minus, Plus, ShoppingCart, Check, Calculator } from "lucide-react";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setActive(0);
    setQty(1);
    setSize(null);
    api.get(`/products/${slug}`).then((r) => setData(r.data)).catch(() => {});
  }, [slug]);

  if (!data) return <div className="pt-40 pb-40 text-center text-zinc-500">Loading...</div>;

  const p = data.product;
  const isTire = p.category === "tires" && p.size_options?.length > 0;
  const currentPrice = isTire && size ? size.price : p.price;
  const grouped = {};
  if (isTire) p.size_options.forEach((o) => { const d = o.size.split("R")[1]; (grouped[d] = grouped[d] || []).push(o); });

  const add = () => {
    if (isTire && !size) {
      toast.error("Please select a tire size first");
      return;
    }
    if (isTire) {
      addItem({ ...p, price: size.price, name: `${p.name} — ${size.size}` }, qty);
      toast.success(`${p.name} (${size.size}) added to cart`, { description: `Qty ${qty} · ${money(size.price * qty)}` });
    } else {
      addItem(p, qty);
      toast.success(`${p.name} added to cart`, { description: `Qty ${qty} · ${money(p.price * qty)}` });
    }
  };

  return (
    <div className="pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-8" data-testid="breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link> /{" "}
          <Link to={`/category/${p.category}`} className="hover:text-white capitalize">{p.category}</Link> /{" "}
          <span className="text-[#00f0ff]">{p.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="aspect-square overflow-hidden border border-[#27272a] bg-gradient-to-br from-[#161616] to-[#080808] relative">
              <img src={p.images[active]} alt={p.name} className="w-full h-full object-contain p-8" data-testid="product-main-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
            </div>
            {p.images.length > 1 && (
            <div className="grid grid-cols-3 gap-3 mt-3">
              {p.images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActive(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square overflow-hidden border ${active === i ? "border-[#00f0ff]" : "border-[#27272a]"} bg-gradient-to-br from-[#161616] to-[#080808]`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain p-2" />
                </button>
              ))}
            </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="text-sm uppercase tracking-[0.2em] text-[#00f0ff]">{p.brand}</div>
            <h1 className="font-display text-5xl md:text-6xl uppercase leading-none mt-2" data-testid="product-title">{p.name}</h1>

            <div className="flex items-center gap-3 mt-4">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={16} className={i < Math.round(p.rating) ? "fill-[#ffd700] text-[#ffd700]" : "text-zinc-600"} />
                ))}
              </div>
              <span className="text-sm text-zinc-400">{p.rating} · {p.reviews} reviews</span>
            </div>

            <div className="text-4xl font-bold mt-6" data-testid="product-price">
              {isTire && !size ? `From ${money(p.price)}` : money(currentPrice)}
            </div>
            <div className="mt-2">
              {p.in_stock ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400"><Check size={15} /> In stock — ships in 24h</span>
              ) : (
                <span className="text-sm text-amber-400">Backorder — ships in 2–3 weeks</span>
              )}
            </div>

            <p className="text-zinc-400 leading-relaxed mt-6">{p.description}</p>

            {isTire && (
              <div className="mt-8" data-testid="tire-size-selector">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm uppercase tracking-[0.2em] text-[#00f0ff]">Select Tire Size</div>
                  {!size ? <span className="text-xs text-amber-400">Required</span> : <span className="text-xs text-emerald-400">{size.size}</span>}
                </div>
                {Object.entries(grouped).map(([dia, opts]) => (
                  <div key={dia} className="mb-3">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{dia}" Wheel</div>
                    <div className="flex flex-wrap gap-2">
                      {opts.map((o) => (
                        <button
                          key={o.size}
                          data-testid={`size-${o.size}`}
                          onClick={() => setSize(o)}
                          className={`px-3 py-2 text-sm border transition-colors ${size?.size === o.size ? "bttf-gradient text-black border-transparent font-semibold" : "border-[#27272a] text-zinc-300 hover:border-[#00f0ff]"}`}
                        >
                          {o.size.split("R")[0]} <span className="opacity-60">· {money(o.price)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center border border-[#27272a]">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="p-3 text-zinc-400 hover:text-white"><Minus size={16} /></button>
                <span className="px-5 font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="p-3 text-zinc-400 hover:text-white"><Plus size={16} /></button>
              </div>
              <button onClick={add} data-testid="add-to-cart" disabled={isTire && !size} className={`flex-1 flex items-center justify-center gap-2 bttf-gradient text-black font-bold uppercase tracking-wider py-4 transition-opacity ${isTire && !size ? "opacity-40 cursor-not-allowed" : "hover:opacity-90"}`}>
                <ShoppingCart size={18} /> {isTire && !size ? "Select a Size" : "Add to Cart"}
              </button>
            </div>

            {(p.category === "rims" || p.category === "tires") && (
              <Link to="/tools" data-testid="tools-link" className="mt-4 inline-flex items-center gap-2 text-sm text-[#00f0ff] hover:underline">
                <Calculator size={15} /> {p.category === "rims" ? "Check offset & backspacing" : "Compare tire sizes"}
              </Link>
            )}

            {/* Specs */}
            <div className="mt-10 border border-[#27272a]">
              <div className="font-display text-2xl uppercase px-5 py-3 border-b border-[#27272a] bg-[#0c0c0c]">Specifications</div>
              <table className="w-full text-sm" data-testid="specs-table">
                <tbody>
                  {Object.entries(p.specs).map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-[#0a0a0a]" : ""}>
                      <td className="px-5 py-3 text-zinc-400 w-1/2">{k}</td>
                      <td className="px-5 py-3 text-white font-medium">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Related */}
        {data.related?.length > 0 && (
          <div className="mt-28">
            <Reveal><h2 className="font-display text-4xl md:text-5xl uppercase mb-10">You May Also <span className="bttf-text">Run</span></h2></Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {data.related.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
